import type { CodeContent } from '@/types/github';
import type { QueryClient } from '@tanstack/react-query';

import { hydrateOmittedCodeContents } from '@/data/v2/pr/hydrate-omitted-code-contents';

function hasUsableTextPatch(file: CodeContent): boolean {
  return typeof file.patch === 'string' && file.patch.trim().length > 0;
}

/**
 * Keep diff patches for prompt construction and `/pr` artifacts, but drop full-file bodies
 * from the chat request to reduce the POST payload size on large pull requests.
 * Files without a textual patch still need old/new bodies so server-side retrieval can
 * build fallback artifacts and localized code context from the request payload.
 */
export function prepareCodeContentForChatRequest(codeContent: CodeContent[]): CodeContent[] {
  return codeContent.map((file) => {
    if (!hasUsableTextPatch(file)) {
      return { ...file };
    }

    return {
      ...file,
      oldContent: undefined,
      newContent: undefined,
    };
  });
}

export type ChatCodeContentPrIdentity = {
  owner: string;
  repo: string;
  number: string;
  // Comparison SHAs of the code-changes payload the files came from; required (nullable) so the
  // hydration layer can pin exact file versions instead of letting the per-file endpoint fall
  // back to canonical summary SHAs. When either is null, hydration is skipped and stripped files
  // are sent as-is.
  headSha: string | null;
  baseSha: string | null;
};

/**
 * Async request-build variant: files stripped from the code-changes payload
 * (`contentsOmitted === true`) that also lack a textual patch would otherwise reach the server
 * with null bodies, breaking the retrieval fallback that builds artifacts from request contents.
 * Rehydrates exactly those files via the lazy per-file endpoint before applying the standard
 * patch-first slimming; a file that fails to hydrate is sent as-is so the request still goes out.
 */
export async function prepareHydratedCodeContentForChatRequest(
  queryClient: QueryClient,
  pr: ChatCodeContentPrIdentity,
  codeContent: CodeContent[],
): Promise<CodeContent[]> {
  const hydrated = await hydrateOmittedCodeContents(
    queryClient,
    pr.owner,
    pr.repo,
    pr.number,
    codeContent,
    {
      headSha: pr.headSha,
      baseSha: pr.baseSha,
      // Files with a usable patch never ship bodies in the request, so hydrating them would be
      // wasted fetches — only the no-patch retrieval-fallback files need their contents back.
      shouldHydrateFile: (file) => !hasUsableTextPatch(file),
    },
  );

  return prepareCodeContentForChatRequest(hydrated);
}
